import React, { useState, useEffect, useCallback, useMemo } from "react";
import dayjs from "dayjs";
import _ from "lodash";
import isToday from "dayjs/plugin/isToday";
import styled from "styled-components";

import { ShopEntity } from "src/firebase/db/Shops";
import BakedBreadsDB, { BakedBreadEntity } from "src/firebase/db/BakedBreads";
import { BakedBreadTagEntity } from "src/firebase/db/BakedBreadTags";

import {
  NotificationTemplateEntity,
  notificationTemplateInitialEntity,
  NotificationTemplateBakedBreadEntity,
  notificationTemplateBakedBreadInitialEntity,
} from "src/firebase/db/NotificationTemplates";

import { getShopFullAddress } from "src/helpers/ShopHelper";
import { roundMaximumQuantity } from "src/helpers/NotificationHelper";
import { formatDateToDate, roundDate } from "src/helpers/UtilityHelper";

import { EVERY_MINUTES } from "src/constants/Notification";
import StyleConfig from "src/constants/StyleConfig";
import { State } from "src/state/ducks";

import SelectBox from "src/components/atoms/form/SelectBox";
import { MyTimePicker } from "src/components/atoms/form/MyDatePicker";
import Button from "src/components/atoms/button/Button";
import TextArea from "src/components/atoms/form/TextArea";
import AnnotaionText from "src/components/atoms/form/AnnotationText";
import { ValidateMessageIfError } from "src/components/atoms/form/ErrorMessage";
import SingleInputFormBlock from "src/components/molecules/form/SingleInputFormBlock";
import InputLabelHolder from "src/components/molecules/form/InputLabelHolder";
import NotificationCreateOrderBread from "src/components/organisms/form/NotificationCreateOrderBread";

dayjs.extend(isToday);

const mapEntityToState = (entities: {
  draft: NotificationTemplateEntity;
  bakedBreads: BakedBreadEntity[];
  shop: ShopEntity;
}) => {
  const { draft, bakedBreads, shop } = entities;

  const { baked_breads: notificationTemplateBakedBreads } = draft;

  const hasDraft = draft.shop_id !== "";
  const order_breads = bakedBreads.map((bakedBread) => {
    const notificationTemplateBakedBread =
      notificationTemplateBakedBreads.find((v) => {
        return v.ref.id === bakedBread.id;
      }) || notificationTemplateBakedBreadInitialEntity;

    return {
      selected: hasDraft ? notificationTemplateBakedBread.selected : false,
      isStopped: bakedBread.is_stopped,
      breadId: bakedBread.id,
      notificationId: "",
      shopId: shop.id,
      shopName: shop.name,
      shopAddress: getShopFullAddress(shop),
      name: bakedBread.name,
      description: bakedBread.description,
      image: bakedBread.image,
      price: bakedBread.price,
      maximumOrder: notificationTemplateBakedBread.maximum_order,
      quantity: notificationTemplateBakedBread.stock,
      userCount: bakedBread.stats.userCount,
      lastNotificationTime: bakedBread.last_notification_time
        ? formatDateToDate(bakedBread.last_notification_time)
        : undefined,
    };
  });
  const now = new Date();
  return {
    ...draft,
    shopId: shop.id,
    pickupSupplement: draft.pickup_supplement,
    scheduledTime: hasDraft
      ? draft.scheduled_time
      : roundDate(
          dayjs(now).add(1, "minute").toDate(),
          EVERY_MINUTES,
          "minute"
        ), // 現在以降でまるめたい
    canOrderTime: hasDraft
      ? draft.can_order_time
      : roundDate(
          dayjs(now)
            .add(EVERY_MINUTES + 1, "minute")
            .toDate(),
          EVERY_MINUTES,
          "minute"
        ),
    lastPickupTime: hasDraft
      ? draft.last_pickup_time
      : roundDate(
          dayjs(now)
            .add(EVERY_MINUTES * 2 + 1, "minute")
            .toDate(),
          EVERY_MINUTES,
          "minute"
        ),
    bakedBreads: order_breads,
  };
};

const mapStateToEntity = async (state: ReturnType<typeof mapEntityToState>) => {
  const bakedBreads = await Promise.all(
    state.bakedBreads.map(
      async (v) =>
        ({
          selected: v.selected,
          ref: await BakedBreadsDB.getDocumentRef(v.breadId),
          maximum_order: v.maximumOrder,
          stock: v.quantity,
        } as NotificationTemplateBakedBreadEntity)
    )
  );

  return {
    ...notificationTemplateInitialEntity,
    id: state.id,
    name: state.name,
    shop_id: state.shopId,
    pickup_supplement: state.pickupSupplement,
    scheduled_time: state.scheduledTime,
    can_order_time: state.canOrderTime,
    last_pickup_time: state.lastPickupTime,
    baked_breads: bakedBreads,
  };
};

type Props = {
  draft: NotificationTemplateEntity;
  selectedTagId: string;
  bakedBreads: BakedBreadEntity[];
  bakedBreadTags: BakedBreadTagEntity[];
  shop: ShopEntity;
  errors: State["notification"]["validationErrors"];
  onChangeTag: (id: string) => void;
  onSubmit: (payload: NotificationTemplateEntity) => void;
};

const NotificationTemplateCreateForm = (props: Props) => {
  const {
    draft,
    selectedTagId,
    bakedBreads,
    bakedBreadTags,
    shop,
    onSubmit,
    onChangeTag,
    errors,
  } = props;

  const [state, setState] = useState(
    mapEntityToState({
      draft,
      bakedBreads,
      shop,
    })
  );

  // stateに初期データ突っ込む
  useEffect(() => {
    if (!bakedBreads.length) {
      return;
    }
    setState(
      mapEntityToState({
        draft,
        bakedBreads,
        shop,
      })
    );
  }, [bakedBreads, shop, draft]);

  const {
    name,
    scheduledTime,
    canOrderTime,
    lastPickupTime,
    pickupSupplement,
    bakedBreads: notificationTemplateBakedBreads,
  } = state;

  const wrapOnSubmit = useCallback(async () => {
    const form = await mapStateToEntity(state);
    onSubmit(form);
  }, [onSubmit, state]);

  // breadIdを元にorderBreadの配列indexを返す
  const getOrderBreadIndex = useCallback(
    (breadId: string) =>
      bakedBreads.findIndex((bakedBread) => bakedBread.id === breadId),
    [bakedBreads]
  );

  // breadIdを元にnotificationTemplateBakedBreadsの個数/上限を更新
  const setBakedBreadsNumberState = useCallback(
    (key: "quantity" | "maximumOrder", breadId: string, num: number | null) => {
      const index = getOrderBreadIndex(breadId);
      notificationTemplateBakedBreads[index][key] = roundMaximumQuantity(num);
      setState((prev) => ({
        ...prev,
        bakedBreads: notificationTemplateBakedBreads,
      }));
    },
    [getOrderBreadIndex, notificationTemplateBakedBreads]
  );

  // breadIdを元にorderBreadの選択状態を更新
  const toggleBakedBreadsSelectedState = useCallback(
    (breadId: string) => {
      const index = getOrderBreadIndex(breadId);
      notificationTemplateBakedBreads[index][
        "selected"
      ] = !notificationTemplateBakedBreads[index]["selected"];
      setState((prev) => ({
        ...prev,
        bakedBreads: notificationTemplateBakedBreads,
      }));
    },
    [getOrderBreadIndex, notificationTemplateBakedBreads]
  );

  // 焼き上がり時刻が入力されているか
  const isEmptyScheduledTime = useMemo(() => scheduledTime === undefined, [
    scheduledTime,
  ]);
  // ラストオーダー時刻が入力されているか
  const isEmptyCanOrderTime = useMemo(() => canOrderTime === undefined, [
    canOrderTime,
  ]);

  // 焼き上がり時刻のmin/max、04:30〜22:30まで
  const scheduledTimeMinTime = dayjs(new Date())
    .hour(4)
    .minute(0)
    .second(0)
    .toDate();
  const scheduledTimeMaxTime = dayjs(new Date())
    .hour(23)
    .minute(15)
    .second(0)
    .toDate();
  // ラストオーダー時刻のmin/max、焼き上がり時刻の30分後〜23:00まで
  const canOrderLastTimeMin = useMemo(
    () => dayjs(scheduledTime).second(0).add(EVERY_MINUTES, "minute").toDate(),
    [scheduledTime]
  );
  const canOrderLastTimeMax = dayjs(new Date())
    .hour(23)
    .minute(30)
    .second(0)
    .toDate();
  // 最終受取時刻のmin/max、ラストオーダー時刻の30分後〜23:30まで
  const lastPickupTimeMin = useMemo(
    () => dayjs(canOrderTime).second(0).add(EVERY_MINUTES, "minute").toDate(),
    [canOrderTime]
  );
  const lastPickupTimeMax = dayjs(new Date())
    .hour(23)
    .minute(45)
    .second(0)
    .toDate();

  // orderBread の個別エラーを取得
  const getBakedBreadErrors = useCallback(
    (breadId: string) => errors[`baked_breads|${breadId}`] || [],
    [errors]
  );

  // カテゴリでパンフィルタリングする
  const filteredOrderBreads = useMemo(() => {
    if (!selectedTagId) {
      return notificationTemplateBakedBreads;
    }

    return notificationTemplateBakedBreads.filter((x) => {
      const bread = bakedBreads.find((y) => x.breadId === y.id);
      const tags = bread?.baked_bread_tags || [];
      return tags.some((v) => v.id === selectedTagId) || x.selected;
    });
  }, [selectedTagId, bakedBreads, notificationTemplateBakedBreads]);

  // パンの有効なカテゴリID
  const activeBreadIds = useMemo(
    () =>
      _(bakedBreads)
        .map(({ baked_bread_tags }) =>
          baked_bread_tags ? baked_bread_tags.map((v) => v.id) : []
        )
        .flatten()
        .uniq()
        .value(),
    [bakedBreads]
  );

  const bakedBreadTagOptions = useMemo(() => {
    const tags = bakedBreadTags
      .map(({ id, name }) => ({ value: id, content: name }))
      .filter((v) => activeBreadIds.includes(v.value));
    tags.unshift({ value: "", content: "全て" });
    return tags;
  }, [activeBreadIds, bakedBreadTags]);

  return (
    <>
      <FormItem>
        <SingleInputFormBlock
          labelName="テンプレート名"
          inputType="text"
          inputValue={name}
          placeholder="（記入例）毎日の配信"
          onChange={(value) => {
            setState((prev) => ({ ...prev, name: value }));
          }}
          isError={false}
          isRequired={true}
          isDisabeld={false}
        />
        <ValidateMessageIfError marginVertical={8} messages={errors.name} />
      </FormItem>
      <FormItem>
        <InputLabelHolder
          labelName="焼き上がりのお知らせ時刻を選択してください"
          isRequired
        />
        <MyTimePicker
          date={scheduledTime}
          dateFormat="HH:mm"
          placeholder="----"
          onChange={(value) =>
            setState((prev) => ({ ...prev, scheduledTime: value }))
          }
          timeInterval={EVERY_MINUTES}
          minTime={scheduledTimeMinTime}
          maxTime={scheduledTimeMaxTime}
        />
        <ValidateMessageIfError
          marginVertical={8}
          messages={errors.scheduled_time}
        />
      </FormItem>
      <FormItem>
        <InputLabelHolder
          labelName="通知するパンを選択してください"
          isRequired
        />
        {bakedBreadTagOptions.length && (
          <SelectBox
            id="bakedBreadTagId"
            placeholder="カテゴリを選択"
            inputValue={selectedTagId}
            onChange={(id) => onChangeTag(id)}
            options={bakedBreadTagOptions}
          />
        )}
        {filteredOrderBreads.map((bread) => (
          <NotificationCreateOrderBread
            key={bread.breadId}
            orderBread={bread}
            onSelect={() => toggleBakedBreadsSelectedState(bread.breadId)}
            onChangeQuantity={(num) =>
              setBakedBreadsNumberState("quantity", bread.breadId, num)
            }
            onChangeMaximumOrder={(num) =>
              setBakedBreadsNumberState("maximumOrder", bread.breadId, num)
            }
            errors={getBakedBreadErrors(bread.breadId)}
          />
        ))}
        <ValidateMessageIfError
          marginVertical={8}
          messages={errors.baked_breads}
        />
      </FormItem>
      <FormItem>
        <InputLabelHolder
          labelName="ラストオーダー時刻を選択してください"
          isRequired
        />
        <MyTimePicker
          date={canOrderTime}
          dateFormat="HH:mm"
          onChange={(value) =>
            setState((prev) => ({ ...prev, canOrderTime: value }))
          }
          timeInterval={EVERY_MINUTES}
          isDisabled={isEmptyScheduledTime}
          minTime={canOrderLastTimeMin}
          maxTime={canOrderLastTimeMax}
        />
        {isEmptyScheduledTime && (
          <ValidateMessageIfError
            marginVertical={8}
            messages={["先に焼き上がりのお知らせ時刻を入力してください。"]}
          />
        )}
        <ValidateMessageIfError
          marginVertical={8}
          messages={errors.can_order_time}
        />
        <AnnotaionText
          text={"※ここで設定した時刻以降、お客様は注文できなくなります。"}
        />
      </FormItem>
      <FormItem>
        <InputLabelHolder
          labelName="最終受取時刻を選択してください"
          isRequired={true}
        />
        <MyTimePicker
          date={lastPickupTime}
          dateFormat="HH:mm"
          placeholder="----"
          onChange={(value) =>
            setState((prev) => ({ ...prev, lastPickupTime: value }))
          }
          timeInterval={EVERY_MINUTES}
          isDisabled={isEmptyCanOrderTime}
          minTime={lastPickupTimeMin}
          maxTime={lastPickupTimeMax}
        />
        {isEmptyCanOrderTime && !scheduledTime && (
          <ValidateMessageIfError
            marginVertical={8}
            messages={["先にラストオーダー時刻を入力してください。"]}
          />
        )}
        <ValidateMessageIfError
          marginVertical={8}
          messages={errors.last_pickup_time}
        />
      </FormItem>
      <FormItem>
        <InputLabelHolder
          labelName="商品を受け渡す際の注意があればご記入ください"
          isRequired={false}
        />
        <TextArea
          rows={10}
          max={300}
          inputValue={pickupSupplement}
          placeholder={
            "(記入例)\n手持ち袋は有料になりますので、エコバッグをお持ち下さい。\nポイントカードをお持ちの方は受け取り時にお声がけください。"
          }
          onChange={(value) =>
            setState((prev) => ({ ...prev, pickupSupplement: value }))
          }
          isError={false}
          isDisabeld={false}
        />
      </FormItem>

      <ButtonWrapper>
        <Button
          isDisabled={false}
          clickEvent={wrapOnSubmit}
          text="確認画面へ"
        />
      </ButtonWrapper>
    </>
  );
};

const FormItem = styled.div`
  &:not(:first-child) {
    margin-top: 24px;
  }
  .registerdUser {
    font-size: 12px;
    color: ${StyleConfig.COLOR.TEXT_COLOR};
    margin: 4px auto;
    span {
      color: ${StyleConfig.COLOR.PRIMARY};
      font-weight: bold;
    }
  }
`;

const ButtonWrapper = styled.div`
  margin-top: 36px;
`;

export default NotificationTemplateCreateForm;
